/**
 * useCampaignFilterOptions
 *
 * Options for the "Enrolled in Campaign" filter dropdowns on the CRM
 * Dashboard (contacts) and My Deals page.
 *
 * Data source: the crm_campaign_filter_options() RPC
 * (supabase/migrations/20260908_01_campaign_enrollment_filter.sql) — active
 * campaign templates plus a distinct count of currently-enrolled contacts
 * (crm_automation_queue status pending/processing), pre-sorted by
 * active_contacts desc, then name.
 *
 * Visibility mirrors fetchTemplates in src/api/supabase/automations.js:
 *  - CRM Admins / SuperAdmins see every active template, including backend
 *    Company Automations (execution_scope='company').
 *  - Everyone else sees company-owned templates plus their own, excluding
 *    Company Automations.
 *
 * getCampaignName resolves ids against the UNFILTERED rows so filter chips
 * (e.g. from a smart list built by an admin) still render a real name even
 * when the option isn't offered in the caller's dropdown.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';

export function useCampaignFilterOptions() {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!isSupabaseConfigured()) {
        setLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase.rpc('crm_campaign_filter_options');
        if (error) throw error;
        if (!cancelled) setRows(data || []);
      } catch (err) {
        console.error('[useCampaignFilterOptions] failed to load campaign options:', err);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const campaignOptions = useMemo(() => {
    const uid = currentUser?.uid;
    return rows
      .filter((row) => {
        if (isCRMAdmin) return true;
        if (row.execution_scope === 'company') return false; // backend-only automations
        return row.ownership_type === 'company' || row.created_by === uid;
      })
      .map((row) => ({
        value: row.template_id,
        label: row.name,
        activeCount: Number(row.active_contacts) || 0,
      }));
  }, [rows, currentUser?.uid, isCRMAdmin]);

  const getCampaignName = useCallback(
    (templateId) => {
      const row = rows.find((r) => r.template_id === templateId);
      return row?.name || 'Unknown campaign';
    },
    [rows]
  );

  return { campaignOptions, getCampaignName, loading };
}

export default useCampaignFilterOptions;
